import React, { useState, useEffect } from "react";
import { FileDown, BarChart3, Image } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import ScoutingReportManager from "../../managers/ScoutingReportsManager";
import { SprayChart } from "../../components/scouting/SprayChart";
import { fetchAPI, fetchAPIBlob } from "../../config/api";
import { saveAs } from "file-saver";
import { toDivisionApi } from "../../config/constants";
import { ReportHeader } from "../../components/reports";
import { computeAggregates } from "../../utils/sprayChartUtils";

export const SprayChartsPage = () => {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sprayChartData, setSprayChartData] = useState({});
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchReportAndData = async () => {
      try {
        setLoading(true);
        const reportData = await ScoutingReportManager.getReportById(reportId);
        setReport(reportData);

        if (reportData) {
          const batters = reportData.positionPlayers || [];
          const playerIds = batters.map(b => b.id).filter(Boolean);

          if (playerIds.length > 0) {
            const bulkData = await fetchAPI('/spraychart_data/bulk', {
              method: 'POST',
              body: JSON.stringify({
                player_ids: playerIds,
                year: reportData.year,
                division: toDivisionApi(reportData.division),
              }),
            });
            setSprayChartData(bulkData || {});
          }
        }
      } catch (error) {
        console.error("Error fetching report:", error);
      } finally {
        setLoading(false);
      }
    };

    if (reportId) fetchReportAndData();
  }, [reportId]);

  const handleExportPdf = async () => {
    if (exporting) return;
    const batters = report?.positionPlayers || [];
    if (batters.length === 0) return;
    setExporting(true);
    try {
      const blob = await fetchAPIBlob('/reports/spray_charts_bulk', {
        method: 'POST',
        body: JSON.stringify({
          players: batters.map(b => ({ id: b.id, name: b.name })),
          year: report?.year,
          division: toDivisionApi(report?.division),
          team_name: report?.teamName || 'team',
        }),
      });
      saveAs(blob, `${report?.teamId || "team"}_spray_charts_${report?.year}.pdf`);
    } catch (error) {
      console.error("Error exporting spray charts:", error);
    } finally {
      setExporting(false);
    }
  };

  const handleExportSingle = async (player) => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = new URLSearchParams({
        player_id: player.id,
        year: report?.year,
        division: toDivisionApi(report?.division),
        hand_filter: "all",
      });
      const blob = await fetchAPIBlob(`/reports/spray_chart?${params.toString()}`);
      saveAs(blob, `${(player.name || 'spraychart').replace(/\s+/g, '_')}_${report?.year}.png`);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-800 mb-2">Report Not Found</h2>
          <button onClick={() => navigate("/scouting")} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Back to Scouting
          </button>
        </div>
      </div>
    );
  }

  const batters = report.positionPlayers || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      <div className="container max-w-6xl mx-auto px-8 sm:px-12 lg:px-16 py-16">
        <ReportHeader
          teamId={report.teamId}
          teamName={report.teamName}
          reportType="Spray Charts"
          year={report.year}
        >
          <button
            onClick={handleExportPdf}
            disabled={exporting || batters.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FileDown size={14} />
            {exporting ? "Exporting…" : "Export PDF"}
          </button>
        </ReportHeader>

        {batters.length === 0 ? (
          <div className="text-center py-16">
            <div className="bg-blue-50 p-8 rounded-xl inline-flex flex-col items-center">
              <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-4">
                <BarChart3 size={24} />
              </div>
              <p className="text-gray-800 text-lg font-medium">No batters in this report</p>
              <p className="text-gray-500 mt-2">Add batters to generate spray charts</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {batters.map((batter) => (
              <div
                key={batter.id}
                className="border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-white"
              >
                <SprayChart
                  playerId={batter.id}
                  year={report.year}
                  division={toDivisionApi(report.division)}
                  prefetchedData={sprayChartData[batter.id]}
                />
                <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm">
                    {(() => {
                      const agg = computeAggregates(sprayChartData[batter.id]?.events || []);
                      const s = agg.stats;
                      return [["AVG", s.battingAvg], ["OBP", s.onBasePercentage], ["SLG", s.slugging]].map(([label, val]) => (
                        <div key={label} className="text-center">
                          <div className="font-semibold text-gray-800">
                            {val != null ? Number(val).toFixed(3).replace(/^0\./, ".") : ".---"}
                          </div>
                          <div className="text-xs text-gray-500">{label}</div>
                        </div>
                      ));
                    })()}
                  </div>
                  <button
                    onClick={() => handleExportSingle(batter)}
                    disabled={exporting}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Image size={14} />
                    PNG
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
